import { Token } from '@uniswap/sdk-core'
import { ChainId } from '@uniswap/smart-order-router'
import { FeeAmount, Pool } from '@uniswap/v3-sdk'
import { SupportedChainId } from 'constants/chains'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { BigNumber } from 'ethers'
import _ from 'lodash'

import { usdGasTokensByChain } from '../constants'
import { IV3PoolProvider } from '../providers/pool-provider'

export async function getHighestLiquidityV3USDPool(
  chainId: SupportedChainId,
  poolProvider: IV3PoolProvider
): Promise<Pool> {
  const usdTokens = usdGasTokensByChain[chainId]
  const wrappedCurrency = WRAPPED_NATIVE_CURRENCY[chainId]!

  if (!usdTokens) {
    throw new Error(`Could not find a USD token for computing gas costs on ${chainId}`)
  }

  const usdPools = _([FeeAmount.HIGH, FeeAmount.MEDIUM, FeeAmount.LOW, FeeAmount.LOWEST])
    .flatMap((feeAmount) => {
      return _.map<Token, [Token, Token, FeeAmount]>(usdTokens, (usdToken) => [wrappedCurrency, usdToken, feeAmount])
    })
    .value()

  const poolAccessor = await poolProvider.getPools(usdPools)

  const pools = _([FeeAmount.HIGH, FeeAmount.MEDIUM, FeeAmount.LOW, FeeAmount.LOWEST])
    .flatMap((feeAmount) => {
      const pools = []

      for (const usdToken of usdTokens) {
        const pool = poolAccessor.getPool(wrappedCurrency, usdToken, feeAmount)
        if (pool) {
          pools.push(pool)
        }
      }

      return pools
    })
    .compact()
    .value()

  if (pools.length == 0) {
    const message = `Could not find a USD/${wrappedCurrency.symbol} pool for computing gas costs.`
    throw new Error(message)
  }

  const maxPool = _.maxBy(pools, (pool) => pool.liquidity) as Pool

  return maxPool
}

export async function getHighestLiquidityV3NativePool(
  token: Token,
  poolProvider: IV3PoolProvider
): Promise<Pool | null> {
  const nativeCurrency = WRAPPED_NATIVE_CURRENCY[token.chainId as ChainId]!

  const nativePools = _([FeeAmount.HIGH, FeeAmount.MEDIUM, FeeAmount.LOW, FeeAmount.LOWEST])
    .map<[Token, Token, FeeAmount]>((feeAmount) => {
      return [nativeCurrency, token, feeAmount]
    })
    .value()

  const poolAccessor = await poolProvider.getPools(nativePools)

  const pools = _([FeeAmount.HIGH, FeeAmount.MEDIUM, FeeAmount.LOW, FeeAmount.LOWEST])
    .map((feeAmount) => {
      return poolAccessor.getPool(nativeCurrency, token, feeAmount)
    })
    .compact()
    .value()

  if (pools.length == 0) {
    return null
  }

  const maxPool = _.maxBy(pools, (pool) => pool.liquidity) as Pool

  return maxPool
}

// based on the code from the optimism OVM_GasPriceOracle contract
export function getL2ToL1GasUsed(data: string, overhead: BigNumber): BigNumber {
  // data is hex encoded
  const dataArr: string[] = data.slice(2).match(/.{1,2}/g)!
  const numBytes = dataArr.length
  let count = 0
  for (let i = 0; i < numBytes; i += 1) {
    const byte = parseInt(dataArr[i]!, 16)
    if (byte == 0) {
      count += 4
    } else {
      count += 16
    }
  }
  const unsigned = overhead.add(count)
  const signedConversion = 68 * 16
  return unsigned.add(signedConversion)
}
